<style>
  /* Undo Bootstrap styles */
  label {
    font-weight: initial;
    margin-bottom: initial;
  }
</style>

<template>
  <v-container
    class="pa-0"
  >
    <v-layout
      align-center
      wrap
    >
      <v-flex
        xs12
        class="py-0"
      >
        <v-data-table
          :headers="headers"
          :items="rates"
          :items-per-page="15"
          :loading="loading"
          :search="search"
          :sort-by="['name']"
          class="elevation-1"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-btn
                color="secondary"
                class="mr-2"
                :disabled="!canUpdateRates"
                @click="newRate()"
              >
                <v-icon left>
                  fa-plus
                </v-icon>
                New Rate
              </v-btn>
              <v-spacer />
              <v-text-field
                v-model="search"
                append-icon="fa-search"
                label="Search"
                hide-details
                single-line
              />
            </v-toolbar>
          </template>

          <template v-slot:item.rateType="{ item }">
            {{ item.rateType | textFrom(rateTypes) }}
          </template>

          <template v-slot:item.payRate="{ item }">
            {{ item.payRate | currency }}
          </template>

          <template v-slot:item.action="{ item }">
            <v-btn
              color="primary lighten-2"
              small
              text
              :disabled="!canUpdateRates"
              @click="editRate(item)"
            >
              <v-icon left>
                fa-edit
              </v-icon>
              edit
            </v-btn>
            <v-btn
              color="warning"
              small
              text
              :disabled="!canUpdateRates"
              @click.stop="(deleting.dialog = true) && (deleting.rate = item)"
            >
              <v-icon left>
                fa-remove
              </v-icon>
              delete
            </v-btn>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>

    <v-dialog
      v-model="editing.dialog"
      max-width="600px"
      persistent
    >
      <v-card v-if="editing.rate">
        <v-card-title>
          <span class="headline">Editing {{ editing.rate.name }}</span>
          <v-spacer />
          <v-btn
            icon
            @click="editing.dialog = false"
          >
            <v-icon>fa-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text v-if="!editing.inProgress">
          <v-form v-model="editing.valid">
            <v-container grid-list-lg>
              <v-layout wrap>
                <v-flex
                  xs12
                  md5
                >
                  <v-text-field
                    v-if="editing.dialog"
                    v-model="editing.rate.name"
                    :counter="90"
                    :rules="[validation.required, validation.maxLength90]"
                    autofocus
                    label="Rate name"
                    required
                  />
                </v-flex>

                <v-flex
                  xs8
                  md5
                >
                  <v-select
                    v-model="editing.rate.rateType"
                    :items="rateTypes"
                    label="Type"
                  />
                </v-flex>

                <v-flex
                  xs4
                  md2
                >
                  <v-text-field
                    v-model="editing.rate.payRate"
                    :rules="[validation.required, validation.payRate]"
                    label="Pay rate"
                    prefix="$"
                    required
                  />
                </v-flex>

                <v-flex
                  xs12
                  md12
                >
                  <div v-if="editing.rate.rateType === 0">
                    <p>Applies when hours worked fall within the range:</p>
                    <v-layout row>
                      <v-text-field
                        v-model="editing.rate.threshold"
                        class="mt-0 pt-0 mx-2"
                        label="From"
                        step="0.25"
                        min="0"
                        type="number"
                        style="width: 60px"
                      />
                      <v-text-field
                        v-model="editing.rate.limit"
                        class="mt-0 pt-0 mx-2"
                        label="To"
                        step="0.25"
                        min="0"
                        max="120"
                        type="number"
                        style="width: 60px"
                      />
                      <v-radio-group
                        v-model="editing.rate.perPayRun"
                        row
                        class="mt-0"
                      >
                        <v-radio
                          label="per week"
                          class="mx-2"
                          :value="true"
                        />
                        <v-radio
                          label="per day"
                          class="mx-2"
                          :value="false"
                        />
                      </v-radio-group>
                    </v-layout>
                  </div>

                  <div v-if="editing.rate.rateType === 1">
                    <p>There are no options for this rate type</p>
                    <div style="height: 46px;" />
                  </div>

                  <div v-if="editing.rate.rateType === 2">
                    <p>Applies between:</p>
                    <v-layout row>
                      <v-menu
                        ref="timePickerStart"
                        v-model="editing.showTimePickerStart"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        :return-value.sync="editing.rate.rateAppliedStart"
                        transition="scale-transition"
                        offset-y
                        full-width
                        max-width="290px"
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on }">
                          <v-text-field
                            :value="editing.rate.rateAppliedStart | addPeriod"
                            class="mt-0 pt-0 mx-2"
                            label="Start time"
                            prepend-icon="fa-clock-o"
                            readonly
                            v-on="on"
                          />
                        </template>
                        <v-time-picker
                          v-if="editing.showTimePickerStart"
                          v-model="editing.rate.rateAppliedStart"
                          :allowed-minutes="m => !(m % 5)"
                          ampm-in-title
                          full-width
                          @click:minute="$refs.timePickerStart.save(editing.rate.rateAppliedStart)"
                        />
                      </v-menu>
                      <v-menu
                        ref="timePickerFinish"
                        v-model="editing.showTimePickerFinish"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        :return-value.sync="editing.rate.rateAppliedFinish"
                        transition="scale-transition"
                        offset-y
                        full-width
                        max-width="290px"
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on }">
                          <v-text-field
                            :value="editing.rate.rateAppliedFinish | addPeriod"
                            class="mt-0 pt-0 mx-2"
                            label="End time"
                            readonly
                            v-on="on"
                          />
                        </template>
                        <v-time-picker
                          v-if="editing.showTimePickerFinish"
                          v-model="editing.rate.rateAppliedFinish"
                          :allowed-minutes="m => !(m % 5)"
                          ampm-in-title
                          full-width
                          @click:minute="$refs.timePickerFinish.save(
                            editing.rate.rateAppliedFinish
                          )"
                        />
                      </v-menu>
                    </v-layout>
                  </div>

                  <div v-if="editing.rate.rateType === 3">
                    <p>Applies on these weekdays:</p>
                    <v-layout row>
                      <v-checkbox
                        v-model="editing.rate.isMonday"
                        label="Mon"
                        class="mx-2 mt-0"
                      />
                      <v-checkbox
                        v-model="editing.rate.isTuesday"
                        label="Tue"
                        class="mx-2 mt-0"
                      />
                      <v-checkbox
                        v-model="editing.rate.isWednesday"
                        label="Wed"
                        class="mx-2 mt-0"
                      />
                      <v-checkbox
                        v-model="editing.rate.isThursday"
                        label="Thu"
                        class="mx-2 mt-0"
                      />
                      <v-checkbox
                        v-model="editing.rate.isFriday"
                        label="Fri"
                        class="mx-2 mt-0"
                      />
                      <v-checkbox
                        v-model="editing.rate.isSaturday"
                        label="Sat"
                        class="mx-2 mt-0"
                      />
                      <v-checkbox
                        v-model="editing.rate.isSunday"
                        label="Sun"
                        class="mx-2 mt-0"
                      />
                    </v-layout>
                  </div>
                </v-flex>
              </v-layout>
            </v-container>
          </v-form>
        </v-card-text>

        <v-card-text v-if="editing.inProgress">
          Please stand by
          <v-progress-linear
            indeterminate
            color="primary"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            :disabled="editing.inProgress || !editing.valid"
            color="accent"
            @click="saveRate(editing.rate)"
          >
            Save
          </v-btn>

          <v-btn
            :disabled="editing.inProgress"
            @click="editing.dialog = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="deleting.dialog"
      max-width="600px"
    >
      <v-card v-if="deleting.rate">
        <v-card-title class="headline">
          Deleting {{ deleting.rate.name }}
        </v-card-title>

        <v-card-text v-if="!deleting.inProgress">
          Are you sure you want to delete this rate?
        </v-card-text>

        <v-card-text v-if="deleting.inProgress">
          Please stand by
          <v-progress-linear
            indeterminate
            color="primary"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            :disabled="deleting.inProgress"
            color="warning"
            @click="deleteRate(deleting.rate)"
          >
            Yes
          </v-btn>

          <v-btn
            :disabled="deleting.inProgress"
            @click="deleting.dialog = false"
          >
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      bottom
      color="error"
      multi-line
    >
      {{ snackbar.text }}
      <v-btn
        dark
        text
        @click="snackbar.show = false"
      >
        Close
      </v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios';

export default {
  components: {
  },

  filters: {
    // TODO: Define filters globally
    currency(value) {
      return new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD' }).format(value);
    },

    addPeriod(value) {
      if (!value) {
        return null;
      }
      let hours = value.substring(0, 2);
      if (+hours > 12) {
        hours -= 12;
        // eslint-disable-next-line prefer-template
        return hours.toString(10).padStart(2, '0') + value.substring(2) + ' PM';
      }
      return `${value} AM`;
    },

    textFrom(value, list) {
      // Search the array for an item with the given value key and return its text
      // Allows a single array for presenting human readable values in data rows, radios and selects
      return list.find(e => e.value === value).text;
    },
  },

  data() {
    return {
      loading: false,

      // TODO: These values should all be in the Vuex store.
      clientId: window.clientId,
      canReadRates: window.canReadRates,
      canUpdateRates: window.canUpdateRates,
      rates: [],

      search: '', // Data table filter
      headers: [ // Data table columns
        { text: 'Name', value: 'name' },
        { text: 'Type', value: 'rateType' },
        {
          align: 'right',
          text: 'Pay Rate',
          value: 'payRate',
        },
        {
          align: 'right',
          sortable: false,
          text: 'Actions',
          value: 'action',
        },
      ],
      rateTypes: [
        { value: 0, text: 'Hourly Rate' },
        { value: 1, text: 'Piece Rate/Allowance' },
        { value: 2, text: 'Time of Day' },
        { value: 3, text: 'Day of Week' },
      ],

      editing: {
        dialog: false, // Show the rate edit modal dialog when true
        inProgress: false, // Show progress bar instead of the dialog contents, disable actions
        rate: null, // The rate to edit/create
        valid: null,
      },

      validation: {
        required: value => !!value || 'Required.',
        maxLength90: value => value.length <= 90 || 'Max 90 characters.',

        // TODO: If you'll need negative values, consider adding support for accounting format
        // with numbers in parentheses e.g. $(2.00)
        // https://www.regextester.com/97725
        payRate: value => /^[0-9]+(\.[0-9]{1,2})?$/.test(value) || 'Invalid pay rate.',
      },

      deleting: {
        dialog: false,
        inProgress: false,
        rate: null,
      },

      snackbar: {
        show: false,
        text: '',
      },
    };
  },

  computed: {
  },

  mounted() {
    this.loadRates();
  },

  methods: {
    async loadRates() {
      this.loading = true;
      try {
        const response = await axios({
          method: 'GET',
          url: '/api/rates/',
          params: {
            clientId: this.clientId,
          },
        });
        this.rates = this.convertRates(response.data.data);
      } catch (error) {
        // TODO: Need to touch/copy this code? Make the snackbar to be a component.
        if (error.response) {
          this.snackbar.text = `Error: ${error.response.status} in ${error.request.responseURL}. Please try refreshing the page, or contact us if the problem persists.`;
        } else {
          this.snackbar.text = 'An unknown error occurred. Please try refreshing the page, or contact us if the problem persists.';
        }
        this.snackbar.show = true;
      } finally {
        this.loading = false;
      }
    },

    convertRates(rates) {
      return rates.map((rate) => {
        const outRate = Object.assign({}, rate);

        // JsonConverter on the .Net side spits integer seconds at us, but then takes back a string
        // for the TimeSpan constructor. We'll operate on strings as they are supported by Vuetify.
        let hours = Math.floor(rate.rateAppliedStart / 3600).toString(10).padStart(2, '0');
        let minutes = ((rate.rateAppliedStart % 3600) / 60).toString(10).padStart(2, '0');
        outRate.rateAppliedStart = `${hours}:${minutes}`;

        hours = Math.floor(rate.rateAppliedFinish / 3600).toString(10).padStart(2, '0');
        minutes = ((rate.rateAppliedFinish % 3600) / 60).toString(10).padStart(2, '0');
        outRate.rateAppliedFinish = `${hours}:${minutes}`;

        return outRate;
      });
    },

    newRate() {
      // Minimal viable rate :)
      this.editing.rate = {
        name: 'New rate',
        payRate: 0,
        rateType: 0,
        perPayRun: true,
      };
      // Show the editor
      this.editing.dialog = true;
    },

    editRate(rate) {
      // Create a copy of the existing rate for editing.
      this.editing.rate = Object.assign({}, rate);
      this.editing.dialog = true;
    },

    async saveRate(rate) {
      this.editing.inProgress = true;
      try {
        if (rate.id) { // Update existing
          await axios({
            method: 'PUT',
            url: `/api/rates/${rate.id}`,
            params: rate,
          });
        } else { // Create new
          await axios({
            method: 'POST',
            url: '/api/rates/',
            data: rate,
          });
        }
      } catch (error) {
        // TODO: Need to touch/copy this code? Make the snackbar to be a component.
        if (error.response) {
          this.snackbar.text = `Error: ${error.response.status} in ${error.request.responseURL}. Please try refreshing the page, or contact us if the problem persists.`;
        } else {
          this.snackbar.text = 'An unknown error occurred. Please try refreshing the page, or contact us if the problem persists.';
        }
        this.snackbar.show = true;
      } finally {
        this.editing.dialog = false;
        this.editing.inProgress = false;
        this.loadRates();
      }
    },

    async deleteRate(rate) {
      this.deleting.inProgress = true;
      try {
        await axios({
          method: 'DELETE',
          url: `/api/rates/${rate.id}`,
        });
      } catch (error) {
        // TODO: Need to touch/copy this code? Make the snackbar to be a component.
        if (error.response) {
          this.snackbar.text = `Error: ${error.response.status} in ${error.request.responseURL}. Please try refreshing the page, or contact us if the problem persists.`;
        } else {
          this.snackbar.text = 'An unknown error occurred. Please try refreshing the page, or contact us if the problem persists.';
        }
        this.snackbar.show = true;
      } finally {
        this.deleting.dialog = false;
        this.deleting.inProgress = false;
        this.loadRates();
      }
    },
  },
};
</script>
